import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const contextExtensionComponent: ComponentDef = {
  id: 'context-extension',
  name: 'Context Extension',
  description: 'Extend the effective context length with techniques like positional interpolation and YaRN.',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 2048, min: 1, max: 131072, step: 1 },
    {
      name: 'variant',
      label: 'Method',
      type: 'select',
      default: 'overview',
      options: [
        { label: 'Overview', value: 'overview' },
        { label: 'Positional Interpolation', value: 'pi' },
        { label: 'YaRN', value: 'yarn' },
        { label: 'NTK-aware', value: 'ntk' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const variant = String(params.variant || 'overview');

    const g = new GraphBuilder();
    g.input('x', [seq_len, d_model]);

    if (variant === 'overview') {
      // Overview: show all three methods side by side, then concat results
      // Method 1: Positional Interpolation
      g.linear('pi_wq', 'x', [d_model, d_model]);
      g.linear('pi_wk', 'x', [d_model, d_model]);
      g.scale('pi_pos', 'pi_wk', 'PI: Linear Interp θ·(L_orig/L_new)');
      g.matmul('pi_qk', 'pi_wq', 'pi_pos', [seq_len, seq_len]);
      g.softmax('pi_attn', 'pi_qk', -1);

      // Method 2: YaRN
      g.linear('yarn_wq', 'x', [d_model, d_model]);
      g.linear('yarn_wk', 'x', [d_model, d_model]);
      g.scale('yarn_pos', 'yarn_wk', 'YaRN: NTK + temperature + attention scaling');
      g.matmul('yarn_qk', 'yarn_wq', 'yarn_pos', [seq_len, seq_len]);
      g.softmax('yarn_attn', 'yarn_qk', -1);

      // Method 3: NTK-aware
      g.linear('ntk_wq', 'x', [d_model, d_model]);
      g.linear('ntk_wk', 'x', [d_model, d_model]);
      g.scale('ntk_pos', 'ntk_wk', 'NTK-aware: Base frequency scaling α^(2/d)');
      g.matmul('ntk_qk', 'ntk_wq', 'ntk_pos', [seq_len, seq_len]);
      g.softmax('ntk_attn', 'ntk_qk', -1);

      // Concatenate all three attention outputs for comparison
      g.concat('out', ['pi_attn', 'yarn_attn', 'ntk_attn'], [seq_len, d_model]);
      g.output('out', 'out');

    } else {
      // PI / YaRN / NTK: individual method
      g.linear('wq', 'x', [d_model, d_model]);
      g.linear('wk', 'x', [d_model, d_model]);
      g.linear('wv', 'x', [d_model, d_model]);
      g.scale('pos', 'wk', 'Extended Position');
      g.matmul('qk', 'wq', 'pos', [seq_len, seq_len]);
      g.scale('scale', 'qk', '1/√d_k');
      g.softmax('softmax', 'scale', -1);
      g.matmul('out', 'softmax', 'wv', [seq_len, d_model]);
      g.output('out', 'out');
    }

    return g.build();
  }
};
